"use client"

import { useState, useEffect } from "react"
import "./SupportManger.css"
import supportService from "../../../services/api/supportService"

const SupportManager = () => {
    const [requests, setRequests] = useState([])
    const [filteredRequests, setFilteredRequests] = useState([])
    const [searchTerm, setSearchTerm] = useState("")
    const [statusFilter, setStatusFilter] = useState("Tất cả")
    const [showForm, setShowForm] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    const [newRequest, setNewRequest] = useState({
        customername: "",
        phone: "",
        email: "",
        address: "",
        request: "",
        status: "pending",
    })

    // Fetch requests on component mount
    useEffect(() => {
        fetchRequests()
    }, [])

    // Fetch all support requests
    const fetchRequests = async () => {
        try {
            setLoading(true)
            const response = await supportService.getAllSupportRequests()
            console.log('Fetched support requests:', response)
            if (response && response.supports && Array.isArray(response.supports)) {
                setRequests(response.supports)
                setFilteredRequests(response.supports)
            } else {
                setRequests([])
                setFilteredRequests([])
            }
        } catch (error) {
            console.error("Error fetching support requests:", error)
            setError("Không thể tải danh sách yêu cầu hỗ trợ")
        } finally {
            setLoading(false)
        }
    }

    // Filter requests based on search term and status
    useEffect(() => {
        let filtered = requests

        if (searchTerm) {
            filtered = filtered.filter(
                (request) =>
                    request.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    request.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    request.request?.toLowerCase().includes(searchTerm.toLowerCase())
            )
        }

        if (statusFilter !== "Tất cả") {
            filtered = filtered.filter((request) => request.status === statusFilter)
        }

        setFilteredRequests(filtered)
    }, [searchTerm, statusFilter, requests])

    // Handle input change for new request form
    const handleInputChange = (e) => {
        const { name, value } = e.target
        setNewRequest({
            ...newRequest,
            [name]: value,
        })
    }

    // Add new request
    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            setLoading(true)
            await supportService.createSupportRequest(newRequest)
            await fetchRequests()
            setShowForm(false)
            setNewRequest({
                customername: "",
                phone: "",
                email: "",
                address: "",
                request: "",
                status: "pending",
            })
        } catch (error) {
            console.error("Error creating support request:", error)
            setError("Không thể tạo yêu cầu hỗ trợ mới")
        } finally {
            setLoading(false)
        }
    }

    // Update request status
    const handleStatusChange = async (id, newStatus) => {
        try {
            setLoading(true)
            const requestToUpdate = requests.find((request) => request._id === id)
            if (requestToUpdate) {
                await supportService.updateSupportRequest(id, { ...requestToUpdate, status: newStatus })
                await fetchRequests()
            }
        } catch (error) {
            console.error("Error updating support request status:", error)
            setError("Không thể cập nhật trạng thái yêu cầu")
        } finally {
            setLoading(false)
        }
    }

    // Delete request
    const handleDelete = async (id) => {
        if (window.confirm("Bạn có chắc chắn muốn xóa yêu cầu này không?")) {
            try {
                setLoading(true)
                await supportService.deleteSupportRequest(id)
                await fetchRequests()
            } catch (error) {
                console.error("Error deleting support request:", error)
                setError("Không thể xóa yêu cầu hỗ trợ")
            } finally {
                setLoading(false)
            }
        }
    }

    return (
        <div className="support-container">
            <h1>Quản Lý Yêu Cầu Hỗ Trợ</h1>
            {error && <div className="error-message">{error}</div>}
            {loading && <div className="loading-spinner">Loading...</div>}

            <div className="support-controls">
                <div className="search-filter">
                    <input
                        type="text"
                        placeholder="Tìm kiếm theo tên, email hoặc dịch vụ..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />

                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="status-filter">
                        <option value="Tất cả">Tất cả trạng thái</option>
                        <option value="pending">Chưa xử lý</option>
                        <option value="complete">Hoàn thành</option>
                    </select>
                </div>

                <button className="add-button" onClick={() => setShowForm(!showForm)}>
                    {showForm ? "Hủy" : "Thêm Yêu Cầu Mới"}
                </button>
            </div>

            {showForm && (
                <div className="request-form-container">
                    <h2>Thêm Yêu Cầu Mới</h2>
                    <form onSubmit={handleSubmit} className="request-form">
                        <div className="form-group">
                            <label htmlFor="customerName">Họ và tên:</label>
                            <input
                                type="text"
                                id="customerName"
                                name="customername"
                                value={newRequest.customername}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="phone">Số điện thoại:</label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={newRequest.phone}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">Email:</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={newRequest.email}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="address">Địa chỉ:</label>
                            <input
                                type="text"
                                id="address"
                                name="address"
                                value={newRequest.address}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="request">Dịch vụ yêu cầu:</label>
                            <select id="request" name="request" value={newRequest.request} onChange={handleInputChange} required>
                                <option value="">Chọn dịch vụ</option>
                                <option value="Tư vấn kỹ thuật">Tư vấn kỹ thuật</option>
                                <option value="Báo lỗi sản phẩm">Báo lỗi sản phẩm</option>
                                <option value="Yêu cầu bảo hành">Yêu cầu bảo hành</option>
                                <option value="Hỗ trợ khác">Hỗ trợ khác</option>
                            </select>
                        </div>

                        <button type="submit" className="submit-button" disabled={loading}>
                            {loading ? "Đang lưu..." : "Lưu Yêu Cầu"}
                        </button>
                    </form>
                </div>
            )}

            <div className="requests-table-container">
                <table className="requests-table">
                    <thead>
                        <tr>
                            <th>Họ và tên</th>
                            <th>Số điện thoại</th>
                            <th>Email</th>
                            <th>Địa chỉ</th>
                            <th>Dịch vụ yêu cầu</th>
                            <th>Trạng thái</th>
                            <th>Thao tác</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredRequests.length > 0 ? (
                            filteredRequests.map((request) => (
                                <tr key={request._id} className={`status-${request.status?.toLowerCase().replace(/\s+/g, "-")}`}>
                                    <td>{request.customername}</td>
                                    <td>{request.phone}</td>
                                    <td>{request.email}</td>
                                    <td>{request.address}</td>
                                    <td>{request.request}</td>
                                    <td>
                                        <select
                                            value={request.status}
                                            onChange={(e) => handleStatusChange(request._id, e.target.value)}
                                            className={`status-select status-${request.status?.toLowerCase().replace(/\s+/g, "-")}`}
                                            disabled={loading}
                                        >
                                            <option value="pending">Chưa xử lý</option>
                                            <option value="complete">Hoàn thành</option>
                                        </select>
                                    </td>
                                    <td>
                                        <button className="delete-button" onClick={() => handleDelete(request._id)} disabled={loading}>
                                            Xóa
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="7" className="no-data">
                                    Không tìm thấy yêu cầu nào
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default SupportManager
